<script setup lang="ts">
import { computed, nextTick, onBeforeUnmount, onMounted, ref, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import ABEvaluation from '../components/ABEvaluation.vue'
import AppSidebar from '../components/AppSidebar.vue'
import ChatMessage from '../components/ChatMessage.vue'
import ConversationActions from '../components/ConversationActions.vue'
import { useAuthStore } from '../stores/auth'
import { useChatStore } from '../stores/chat'

const route = useRoute()
const router = useRouter()
const auth = useAuthStore()
const chat = useChatStore()
const input = ref('')
const messageList = ref<HTMLElement | null>(null)
const sidebarOpen = ref(false)
const evaluating = ref(false)
const feedbackSavingId = ref('')

const groupedMessages = computed(() => {
  const turns = [...new Set(chat.messages.map(m=>m.turn))].sort((a,b)=>a-b)
  return turns.map(turn=>{
    const rows=chat.messages.filter(m=>m.turn===turn)
    const rounds=[...new Set(rows.filter(m=>m.public_label).map(m=>m.ab_round||1))].sort((a,b)=>a-b)
    return {turn,regular:rows.filter(m=>!m.public_label),rounds:rounds.map(round=>({round,messages:rows.filter(m=>m.public_label&&(m.ab_round||1)===round)}))}
  })
})

async function ensureConversation() {
  await chat.loadConversations()
  const routeId = String(route.params.conversationId || '')
  if (routeId) {
    await chat.openConversation(routeId)
    return
  }
  if (chat.conversations.length) {
    await router.replace(`/chat/${chat.conversations[0].id}`)
  } else {
    const conversation = await chat.createConversation()
    await router.replace(`/chat/${conversation.id}`)
  }
}

async function newConversation() {
  const conversation = await chat.createConversation()
  sidebarOpen.value = false
  await router.push(`/chat/${conversation.id}`)
}

async function selectConversation(id: string) {
  sidebarOpen.value = false
  await router.push(`/chat/${id}`)
}

async function send() {
  const content = input.value.trim()
  if (!content || chat.streaming || chat.inputBlocked) return
  input.value = ''
  try {
    await chat.sendMessage(content)
  } catch {
    input.value = content
  }
}

async function chooseEvaluation(choice: string, comment: string) {
  evaluating.value = true
  try {
    await chat.evaluate(choice, comment)
  } finally {
    evaluating.value = false
  }
}

async function saveFeedback(
  messageId: string,
  reaction: '' | 'like' | 'dislike',
  comment: string,
) {
  feedbackSavingId.value = messageId
  try {
    await chat.saveMessageFeedback(messageId, reaction, comment)
  } finally {
    feedbackSavingId.value = ''
  }
}

async function retryProviderGeneration() {
  try {
    await chat.retryProviderGeneration()
  } catch {
    // The store exposes the user-facing error next to the composer.
  }
}

function handleKeydown(event: KeyboardEvent) {
  if (event.key === 'Enter' && !event.shiftKey) {
    event.preventDefault()
    send()
  }
}

function logout() {
  chat.dispose()
  auth.logout()
  router.push('/login')
}

watch(
  () => route.params.conversationId,
  async (id) => {
    if (id) await chat.openConversation(String(id))
  },
)

watch(
  () => chat.messages.map((item) => item.content.length).join(','),
  async () => {
    await nextTick()
    messageList.value?.scrollTo({
      top: messageList.value.scrollHeight,
      behavior: chat.streaming ? 'auto' : 'smooth',
    })
  },
)

onMounted(ensureConversation)
onBeforeUnmount(() => chat.dispose())
</script>

<template>
  <main class="chat-shell">
    <AppSidebar
      :nickname="auth.nickname"
      :username="auth.username"
      :conversations="chat.conversations"
      :current-id="chat.currentConversation?.id"
      :citations="chat.latestCitations"
      :open="sidebarOpen"
      @new-conversation="newConversation"
      @select="selectConversation"
      @logout="logout"
      @close="sidebarOpen = false"
      @renamed="chat.renamed"
    />

    <section class="chat-workspace">
      <header class="chat-topbar">
        <button
          class="mobile-menu"
          type="button"
          aria-label="打开侧边栏"
          @click="sidebarOpen = true"
        >
          ☰
        </button>
        <div>
          <p class="eyebrow">互动学习空间</p>
          <h1>{{ chat.currentConversation?.title || '计量客栈' }}</h1>
        </div>
        <ConversationActions v-if="chat.currentConversation" :conversation="chat.currentConversation" @renamed="chat.renamed" />
        <span class="knowledge-status">
          <i></i> 基于计量经济学基础知识库
        </span>
      </header>

      <div ref="messageList" class="message-list">
        <section v-if="!chat.messages.length" class="welcome-card">
          <p class="eyebrow">今天想弄懂什么？</p>
          <h2>从一个让你困惑的识别问题开始。</h2>
          <p>
            我会优先检索教材，并告诉你应该继续阅读哪本书的哪一章。
            新对话的第一个问题会展示两份匿名回答供你比较。
          </p>
          <div class="suggestion-grid">
            <button type="button" @click="input = 'OLS 无偏需要哪些关键假设？'">
              <span>01</span> OLS 无偏需要哪些关键假设？
            </button>
            <button type="button" @click="input = 'DID 的平行趋势假设应该怎样理解？'">
              <span>02</span> 如何理解 DID 平行趋势？
            </button>
            <button type="button" @click="input = '请给我一条系统学习工具变量的阅读路线。'">
              <span>03</span> 系统学习工具变量
            </button>
          </div>
        </section>

        <template v-else>
          <section v-for="group in groupedMessages" :key="group.turn" class="question-group">
            <ChatMessage v-for="message in group.regular" :key="message.id" :message="message" :feedback-disabled="feedbackSavingId===message.id" @feedback="saveFeedback" />
            <section v-for="round in group.rounds" :key="`${group.turn}:${round.round}`" class="comparison-round">
              <p class="comparison-round-label">问题 {{group.turn}} · 第 {{round.round}} 轮回答</p>
              <div class="comparison-grid"><ChatMessage v-for="message in round.messages" :key="message.id" :message="message" :feedback-disabled="true" /></div>
            </section>
          </section>

          <ABEvaluation
            v-if="chat.requiresEvaluation"
            :disabled="evaluating"
            @choose="chooseEvaluation"
          />

          <section v-if="chat.isTerminated" class="flow-notice terminated">
            <h3>本对话已结束</h3>
            <p>
              你已连续 {{ chat.currentConversation?.ab_round || 0 }} 轮选择“都不行”。
              系统已标记该记录供管理员复核，请新建对话后继续学习。
            </p>
          </section>

          <section v-if="chat.needsProviderRetry || chat.isIncomplete || chat.canContinue" class="flow-notice">
            <h3>已有记录已保留</h3>
            <p>可以重试未完成的回答，也可以在本对话继续提问。失败的一轮不计入有效评价。</p>
            <button v-if="chat.currentConversation?.can_retry" type="button" :disabled="chat.streaming" @click="retryProviderGeneration">重试未完成回答</button>
            <button v-if="chat.canContinue" class="continue-chat" type="button" @click="chat.continueConversation">继续提问</button>
          </section>
        </template>
      </div>

      <footer class="composer-wrap">
        <p v-if="chat.providerMessage" class="composer-status">
          {{ chat.providerMessage }}
        </p>
        <p v-if="chat.error" class="composer-error">{{ chat.error }}</p>
        <div class="composer" :class="{ disabled: chat.inputBlocked }">
          <textarea
            v-model="input"
            rows="1"
            :disabled="chat.streaming || chat.inputBlocked"
            :placeholder="
              chat.isTerminated
                ? '本对话已结束，请新建对话'
                : chat.isIncomplete
                  ? '首次比较未完成，请新建对话'
                : chat.requiresEvaluation
                  ? '请先完成上方的匿名评价'
                  : chat.needsProviderRetry
                    ? '请先重试本轮原模型回答'
                  : chat.needsABRetry
                    ? '正在等待下一组回答'
                    : '输入你的计量经济学问题…'
            "
            @keydown="handleKeydown"
          ></textarea>
          <button
            type="button"
            :disabled="!input.trim() || chat.streaming || chat.inputBlocked"
            aria-label="发送问题"
            @click="send"
          >
            {{ chat.streaming ? '···' : '↑' }}
          </button>
        </div>
        <p class="composer-note">
          回答可能有误，请结合教材引用核对。不要在对话中提供真实姓名或其他个人信息。
        </p>
      </footer>
    </section>
  </main>
</template>
